import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api, type CasoDeContacto, type Contacto } from "@/lib/api";
import Modal from "@/ui/Modal";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

type Props = {
  open: boolean;
  onClose: () => void;
  contacto: Contacto;
  clienteNombre: string;
};

/** Lista los casos donde este contacto participo — como contacto del caso
 * o por al menos una interaccion registrada. Util para auditoria:
 * "de Acme, en que casos ha intervenido Juan Perez este ano".
 *
 * Cada caso es un link al detalle (cierra el modal y navega).
 */
export default function CasosDelContactoModal({
  open,
  onClose,
  contacto,
  clienteNombre,
}: Props) {
  const toast = useToast();
  const [items, setItems] = useState<CasoDeContacto[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setItems(null);
    api
      .get<CasoDeContacto[]>(`/api/contactos/${contacto.id}/casos`)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude cargar los casos del contacto", msg);
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `toast` cambia de referencia en cada render del provider — si lo
    // dejamos en deps, el setItems(null) re-renderea, el effect dispara,
    // si la API falla setea toast → loop infinito. Es estable en runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contacto.id]);

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={`Casos · ${contacto.nombre}`}
      description={`${clienteNombre} — donde este contacto fue el reportante o tuvo interacciones registradas.`}
      size="lg"
      footer={
        <button onClick={onClose} className="btn-ghost">
          Cerrar
        </button>
      }
    >
      {loading ? (
        <SkeletonRows rows={4} cols={3} />
      ) : items === null || items.length === 0 ? (
        <p className="text-sm text-zinc-500 py-2">
          Este contacto aún no aparece en ningún caso ni interacción.
        </p>
      ) : (
        <ul className="divide-y divide-ink-700">
          {items.map((it) => (
            <li key={it.caso_id} className="py-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/casos/${it.folio}`}
                      onClick={onClose}
                      className="text-sm font-medium text-accent-400 hover:text-accent-300 hover:underline"
                    >
                      {it.folio}
                    </Link>
                    <EstadoChip estado={it.estado} />
                    {it.rol_en_caso === "principal" && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-500/30 bg-accent-500/10 text-accent-300">
                        Principal
                      </span>
                    )}
                    {it.rol_en_caso === "seguimiento" && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-zinc-600 bg-zinc-700/30 text-zinc-300">
                        Seguimiento
                      </span>
                    )}
                    {it.num_interacciones > 0 && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-zinc-600 bg-zinc-700/30 text-zinc-300">
                        {it.num_interacciones} interacción
                        {it.num_interacciones === 1 ? "" : "es"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400 mt-1 truncate">
                    {it.marca || "—"} {it.modelo || ""}
                    {it.ticket_externo && (
                      <span className="text-zinc-500"> · {it.ticket_externo}</span>
                    )}
                  </p>
                </div>
                <div className="text-xs text-zinc-500 lg:text-right lg:flex-shrink-0 whitespace-nowrap">
                  <div>Abierto {fmtDate(it.created_at)}</div>
                  {it.ultima_interaccion && (
                    <div className="text-zinc-400">
                      Última interacción {fmtDate(it.ultima_interaccion)}
                    </div>
                  )}
                  {it.closed_at && (
                    <div>Cerrado {fmtDate(it.closed_at)}</div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

function EstadoChip({ estado }: { estado: string }) {
  const cerrado = estado.toLowerCase().includes("cerrado");
  const cls = cerrado
    ? "border-zinc-600 bg-zinc-700/30 text-zinc-300"
    : "border-amber-500/30 bg-amber-500/10 text-amber-300";
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}>
      {estado}
    </span>
  );
}
